import type {
  AffectedArea,
  CameraFeed,
  DashboardStats,
  FloodAlert,
  HistoricalData,
  LandslideRisk,
  RegionalStats,
  ReservoirData,
} from '@/features/realtime/types/realtime.types';

function pickNum(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return undefined;
}

function pickStr(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string') return v;
  }
  return undefined;
}

/** Chuẩn hoá severity/status từ DB → union FE. */
export function normalizeFloodSeverity(raw: unknown): FloodAlert['severity'] {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (s === 'severe' || s === 'danger') return 'severe';
  if (s === 'warning' || s === 'warn') return 'warning';
  return 'normal';
}

export function normalizeFloodAlertStatus(raw: unknown): FloodAlert['status'] {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (s === 'active') return 'active';
  if (s === 'resolved' || s === 'closed') return 'resolved';
  return 'monitored';
}

export function mapHistoricalDto(raw: unknown, fallbackYear: string): HistoricalData {
  if (!raw || typeof raw !== 'object') {
    return {
      year: fallbackYear,
      peakLevel: 0,
      rainfallTotal: 0,
      affectedArea: 0,
      affectedPop: 0,
      daysFlooded: 0,
      events: [],
    };
  }
  const o = raw as Record<string, unknown>;
  const eventsRaw = o.events;
  const events = Array.isArray(eventsRaw)
    ? eventsRaw.map((e) => {
        if (!e || typeof e !== 'object') {
          return { time: '', event: '', severity: 'normal' };
        }
        const ev = e as Record<string, unknown>;
        return {
          time: String(ev.time ?? ev.t ?? ''),
          event: String(ev.event ?? ev.message ?? ''),
          severity: String(ev.severity ?? 'normal'),
        };
      })
    : [];

  return {
    year: String(o.year ?? fallbackYear),
    peakLevel: pickNum(o, ['peakLevel', 'peak_level']) ?? 0,
    rainfallTotal: pickNum(o, ['rainfallTotal', 'rainfall_total']) ?? 0,
    affectedArea: pickNum(o, ['affectedArea', 'affected_area']) ?? 0,
    affectedPop: pickNum(o, ['affectedPop', 'affected_pop']) ?? 0,
    damageCost: pickStr(o, ['damageCost', 'damage_cost']),
    daysFlooded: pickNum(o, ['daysFlooded', 'days_flooded']) ?? 0,
    events,
  };
}

export function mapRegionalStatDto(raw: unknown): RegionalStats | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const risk = String(o.riskLevel ?? o.risk_level ?? 'low').toLowerCase();
  const riskLevel: RegionalStats['riskLevel'] =
    risk === 'high' ? 'high' : risk === 'medium' ? 'medium' : 'low';
  return {
    regionId: String(o.regionId ?? o.region_id ?? ''),
    regionName: String(o.regionName ?? o.region_name ?? ''),
    alertCount: pickNum(o, ['alertCount', 'alert_count']) ?? 0,
    severeCount: pickNum(o, ['severeCount', 'severe_count']) ?? 0,
    riskLevel,
    affectedPop: pickNum(o, ['affectedPop', 'affected_pop']) ?? 0,
  };
}

export function mapReservoirDto(raw: unknown): ReservoirData | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const st = String(o.status ?? 'normal').toLowerCase();
  const status: ReservoirData['status'] =
    st === 'critical' ? 'critical' : st === 'discharging' ? 'discharging' : 'normal';
  return {
    id: String(o.id ?? ''),
    name: String(o.name ?? ''),
    inflow: pickNum(o, ['inflow']) ?? 0,
    outflow: pickNum(o, ['outflow']) ?? 0,
    currentLevel: pickNum(o, ['currentLevel', 'current_level']) ?? 0,
    maxLevel: pickNum(o, ['maxLevel', 'max_level']) ?? 0,
    status,
  };
}

export function mapLandslideDto(raw: unknown): LandslideRisk | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  let position: [number, number] = [0, 0];
  const p = o.position;
  if (Array.isArray(p) && p.length >= 2) {
    position = [Number(p[0]), Number(p[1])];
  } else {
    const lng = pickNum(o, ['lng', 'lon']);
    const lat = pickNum(o, ['lat']);
    if (lng != null && lat != null) position = [lng, lat];
  }
  const rl = String(o.riskLevel ?? o.risk_level ?? 'medium').toLowerCase();
  const riskLevel: LandslideRisk['riskLevel'] =
    rl === 'high' ? 'high' : rl === 'low' ? 'low' : 'medium';
  return {
    id: String(o.id ?? ''),
    position,
    locationName: String(o.locationName ?? o.location_name ?? ''),
    riskLevel,
    rainfallAccumulated: pickNum(o, ['rainfallAccumulated', 'rainfall_accumulated']) ?? 0,
  };
}

export function mapFloodAlertDto(raw: unknown): FloodAlert | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const ts =
    typeof o.timestamp === 'number'
      ? o.timestamp
      : typeof o.event_at === 'string'
        ? new Date(o.event_at).getTime()
        : Date.now();
  return {
    id: String(o.id ?? ''),
    zoneName: String(o.zoneName ?? o.zone_name ?? ''),
    depthM: pickNum(o, ['depthM', 'depth_m']) ?? 0,
    severity: normalizeFloodSeverity(o.severity),
    status: normalizeFloodAlertStatus(o.status),
    timestamp: Number.isFinite(ts) ? ts : Date.now(),
    affectedPop: pickNum(o, ['affectedPop', 'affected_pop']),
    recommendedAction: pickStr(o, ['recommendedAction', 'recommended_action']),
  };
}

export function mapCameraFeedDto(raw: unknown): CameraFeed | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  let position: [number, number] = [0, 0];
  const p = o.position;
  if (Array.isArray(p) && p.length >= 2) {
    position = [Number(p[0]), Number(p[1])];
  }
  const lu =
    typeof o.lastUpdated === 'number'
      ? o.lastUpdated
      : typeof o.last_updated === 'string' || o.last_updated instanceof Date
        ? new Date(o.last_updated as string).getTime()
        : Date.now();
  const st = String(o.status ?? 'offline').toLowerCase();
  const status: CameraFeed['status'] =
    st === 'online' ? 'online' : st === 'maintenance' ? 'maintenance' : 'offline';
  return {
    id: String(o.id ?? ''),
    name: String(o.name ?? ''),
    position,
    district: String(o.district ?? ''),
    status,
    lastUpdated: Number.isFinite(lu) ? lu : Date.now(),
    snapshotUrl: String(o.snapshotUrl ?? o.snapshot_url ?? ''),
  };
}

export function mapAffectedAreaDto(raw: unknown): AffectedArea | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const st = String(o.status ?? 'warning').toLowerCase();
  const status: AffectedArea['status'] = st === 'severe' ? 'severe' : 'warning';
  return {
    name: String(o.name ?? ''),
    depthM: pickNum(o, ['depthM', 'depth_m']) ?? 0,
    pop: pickNum(o, ['pop']) ?? 0,
    status,
  };
}

export function mapDashboardStatsDto(raw: unknown): DashboardStats {
  if (!raw || typeof raw !== 'object') {
    return {
      alertCount: 0,
      sensorsOnline: 0,
      floodZones: 0,
      areaMonitored: 0,
    };
  }
  const o = raw as Record<string, unknown>;
  return {
    alertCount: pickNum(o, ['alertCount', 'alert_count']) ?? 0,
    sensorsOnline: pickNum(o, ['sensorsOnline', 'sensors_online']) ?? 0,
    floodZones: pickNum(o, ['floodZones', 'flood_zones']) ?? 0,
    areaMonitored: pickNum(o, ['areaMonitored', 'area_monitored']) ?? 0,
  };
}
